/**
 * Форматира една дестинация (API/Prisma shape) в формата за canvas/selectedBook.
 * Същият формат като useApiData formattedDestinations[dest.name].
 */

import { getSiteConfig } from '../config/sites';

const site = getSiteConfig();

const DEFAULT_COVER_TITLE_Y = 18;
const DEFAULT_COVER_TITLE_SIZE = 82;
const DEFAULT_COVER_YEAR_SIZE = 24;

/** Prisma Decimal не може да се подава към Client Components – преобразуваме в number. */
function toNumber(value, fallback = 100) {
  if (value == null) {
    return fallback;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'object' && typeof value.toNumber === 'function') {
    return value.toNumber();
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

export function formatSingleDestinationForCanvas(dest) {
  if (!dest) {
    return null;
  }
  const activeTemplate = dest.templates?.[0];
  const defaultBg = activeTemplate?.backgroundColor || dest.backgroundColor || '#fff';

  return {
    backgroundColor: dest.backgroundColor || defaultBg,
    fontFamily: dest.fontFamily || 'inherit',
    collection: dest.collection?.name ?? 'Unknown',
    coverBackground: dest.backgroundColor || defaultBg,
    textColor: dest.destinationNameColor || '#000',
    yearColor: dest.destinationDescriptionColor || '#666',
    coverTextColor: dest.destinationNameColor || '#000',
    coverYearColor: dest.destinationDescriptionColor || '#666',
    spineTextColor: dest.destinationNameColor || '#000',
    backTextColor: dest.destinationNameColor || '#000',

    pages: dest.layoutPages || [{ id: 1, layout: [], images: [], backgroundColor: defaultBg }],

    texts: {
      coverTitle: dest.coverTitle || dest.name?.toUpperCase() || '',
      spineTitle: dest.spineTitle || dest.name || '',
      backTitle: dest.backTitle || dest.name || '',
      destinationName: dest.destinationName || dest.name || '',
      destinationDescription: dest.destinationDescription || '',
    },

    destinationPage: {
      mapImage: dest.mapImage || site.assets.mapFallback,
      destinationName: dest.destinationName || dest.name || '',
      destinationDescription: dest.destinationDescription || '',
      destinationNameColor: dest.destinationNameColor || '#000',
      destinationDescriptionColor: dest.destinationDescriptionColor || '#666',
      objectFit: dest.objectFit || 'contain',
      imageWidth: dest.imageWidth || '90%',
    },

    details: {
      title: dest.title || dest.name || '',
      pages: dest.pages ?? 5,
      dimensions: dest.dimensions || '26 / 20',
      price: toNumber(dest.price, 100),
      imagePath: dest.imagePath || site.assets.textureFallback,
      thumbnailPath: dest.thumbnailPath || site.assets.textureFallback,
      templates: (dest.templates || []).map((template) => ({
        id: template.templateId ?? template.id,
        templateId: template.templateId,
        image: template.image || site.assets.textureFallback,
        thumbnailSm: template.thumbnailSm ?? null,
        thumbnailMd: template.thumbnailMd ?? null,
        thumbnailLg: template.thumbnailLg ?? null,
        description: template.description || 'Template',
        seoH1: template.seoH1 ?? null,
        coverSlug: template.coverSlug ?? null,
        coverTitle: template.coverTitle ?? null,
        coverYear: template.coverYear ?? null,
        spineTitle: template.spineTitle ?? null,
        backTitle: template.backTitle ?? null,
        backYear: template.backYear ?? null,
        coverBackground: template.backgroundColor || template.coverBackground || defaultBg,
        backgroundColor: template.backgroundColor || defaultBg,
        textColor: template.textColor ?? null,
        yearColor: template.yearColor ?? null,
        coverTextColor: template.coverTextColor ?? null,
        coverYearColor: template.coverYearColor ?? null,
        spineTextColor: template.spineTextColor ?? null,
        backTextColor: template.backTextColor ?? null,
        backYearColor: template.backYearColor ?? null,
        copyrightTextColor: template.copyrightTextColor ?? null,
        logoType: template.logoType ?? 'dark',
        objectFit: template.objectFit || 'contain',
        imageWidth: template.imageWidth || '100%',
        yearTextAlign: template.yearTextAlign || 'center',
        coverTitleX: template.coverTitleX ?? 50,
        coverTitleY: template.coverTitleY ?? 8,
        coverTitleLineHeight: template.coverTitleLineHeight ?? null,
        coverTitleAlign: template.coverTitleAlign ?? null,
        coverYearX: template.coverYearX ?? 50,
        coverYearY: template.coverYearY ?? DEFAULT_COVER_TITLE_Y,
        coverTitleSize: template.coverTitleSize ?? DEFAULT_COVER_TITLE_SIZE,
        coverYearSize: template.coverYearSize ?? DEFAULT_COVER_YEAR_SIZE,
        fontFamily: template.fontFamily || dest.fontFamily || site.branding.fontFamilyName,
        titleFontFamily:
          template.titleFontFamily ||
          template.fontFamily ||
          dest.fontFamily ||
          site.branding.fontFamilyName,
        yearFontFamily:
          template.yearFontFamily ||
          template.fontFamily ||
          dest.fontFamily ||
          site.branding.fontFamilyName,
      })),
      quickInfo: dest.quickInfo || {
        dimensions: {
          value: dest.dimensions || '26 / 20',
          description: '',
        },
        pages: { value: dest.pages ?? 5, description: '' },
        price: { value: toNumber(dest.price, 100), description: '' },
        templates: {
          count: (dest.templates || []).length,
          description: '',
        },
      },
    },

    slug: dest.slug,
    isActive: dest.isActive,
    order: dest.order,
    activeTemplateId: dest.templates?.[0]?.templateId ?? null,
  };
}
